/**
 * Lead pipeline.
 *
 * Only the transitions the API permits from the record's current status are offered, so the UI
 * cannot suggest an action that will be rejected. Selecting a lead opens a detail drawer with the
 * full submission, status history and internal notes.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Modal, Select, Spinner, Textarea } from '@/components/ui';
import { DataTable, type Column } from '@/components/admin/DataTable';
import { LoadError } from '@/components/site/blocks';
import { useDebounced } from '@/hooks';
import { cmsApi } from '@/services/api';
import { ApiError } from '@/services/client';
import type { LeadRecord } from '@/services/types';

const STATUS_TONES: Record<string, 'success' | 'signal' | 'primary' | 'neutral' | 'danger'> = {
  new: 'primary',
  contacted: 'signal',
  qualified: 'signal',
  in_progress: 'signal',
  converted: 'success',
  closed: 'neutral',
  spam: 'danger',
};

function LeadDrawer({ leadId, onClose }: { leadId: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [nextStatus, setNextStatus] = useState('');
  const [error, setError] = useState('');

  const { data, isLoading, refetch } = useQuery({ queryKey: ['cms', 'lead', leadId], queryFn: () => cmsApi.lead(leadId) });

  const statusMutation = useMutation({
    mutationFn: () => cmsApi.updateLeadStatus(leadId, nextStatus, statusNote),
    onSuccess: () => {
      setNextStatus('');
      setStatusNote('');
      setError('');
      void refetch();
      void queryClient.invalidateQueries({ queryKey: ['cms', 'leads'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'The status change failed.'),
  });

  const noteMutation = useMutation({
    mutationFn: () => cmsApi.addLeadNote(leadId, note),
    onSuccess: () => {
      setNote('');
      void refetch();
    },
  });

  return (
    <Modal open onClose={onClose} title={data ? `${data.reference} — ${data.full_name}` : 'Lead'} size="lg">
      {isLoading || !data ? (
        <Spinner label="Loading lead" />
      ) : (
        <div className="space-y-6">
          {error && <Alert tone="danger">{error}</Alert>}

          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={STATUS_TONES[data.status] ?? 'neutral'}>{data.status_label}</Badge>
            <Badge tone="neutral">{data.lead_type_label}</Badge>
            <Badge tone={data.priority === 'urgent' || data.priority === 'high' ? 'signal' : 'neutral'}>{data.priority}</Badge>
            {data.spam_score >= 0.4 && <Badge tone="warning">Spam score {data.spam_score}</Badge>}
          </div>

          <dl className="grid gap-4 sm:grid-cols-2">
            {[
              { label: 'Email', value: <a href={`mailto:${data.email}`}>{data.email}</a> },
              { label: 'Phone', value: data.phone || '—' },
              { label: 'Organisation', value: data.company || '—' },
              { label: 'Role', value: data.job_title || '—' },
              { label: 'Organisation type', value: data.organisation_type || '—' },
              { label: 'Country', value: data.country || '—' },
              { label: 'Budget', value: data.budget_range || '—' },
              { label: 'Timeline', value: data.timeline || '—' },
              { label: 'Source page', value: data.source_page || '—' },
              { label: 'Campaign', value: data.utm_campaign || data.utm_source || 'Direct' },
              { label: 'Assigned to', value: data.assigned_to_name || 'Unassigned' },
              { label: 'Received', value: new Date(data.created_at).toLocaleString('en-IN') },
            ].map((item) => (
              <div key={item.label}>
                <dt className="text-xs text-muted">{item.label}</dt>
                <dd className="mt-0.5 text-sm text-ink">{item.value}</dd>
              </div>
            ))}
          </dl>

          {(data.interest_product_names.length > 0 || data.interest_solution_names.length > 0) && (
            <div>
              <p className="text-xs text-muted">Interested in</p>
              <ul className="mt-2 flex flex-wrap gap-2">
                {[...data.interest_solution_names, ...data.interest_product_names].map((name) => (
                  <li key={name}>
                    <Badge tone="primary">{name}</Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.message && (
            <div>
              <p className="text-xs text-muted">Message</p>
              <p className="mt-2 whitespace-pre-wrap rounded-md bg-raised p-4 text-sm leading-relaxed text-ink">{data.message}</p>
            </div>
          )}

          <div className="rounded-lg border border-line p-4">
            <h3 className="text-sm font-semibold text-ink">Move this lead forward</h3>
            {data.allowed_next_statuses.length === 0 ? (
              <p className="mt-2 text-sm text-muted">This lead has reached a final status.</p>
            ) : (
              <div className="mt-3 space-y-3">
                <Select value={nextStatus} onChange={(event) => setNextStatus(event.target.value)} aria-label="Next status">
                  <option value="">Choose the next status…</option>
                  {data.allowed_next_statuses.map((status) => (
                    <option key={status} value={status}>
                      {status.replace('_', ' ')}
                    </option>
                  ))}
                </Select>
                <Textarea
                  rows={2}
                  value={statusNote}
                  onChange={(event) => setStatusNote(event.target.value)}
                  placeholder="Optional note recorded against the change"
                  aria-label="Status change note"
                />
                <Button size="sm" disabled={!nextStatus} loading={statusMutation.isPending} onClick={() => statusMutation.mutate()}>
                  Update status
                </Button>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-ink">Internal notes</h3>
            {data.notes.length > 0 && (
              <ul className="mt-3 space-y-3">
                {data.notes.map((item) => (
                  <li key={item.id} className="rounded-md bg-raised p-3">
                    <p className="text-sm text-ink">{item.body}</p>
                    <p className="mt-1 text-xs text-muted">
                      {item.author_name} · {new Date(item.created_at).toLocaleString('en-IN')}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 space-y-2">
              <Textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Add a note…" aria-label="New note" />
              <Button size="sm" variant="secondary" disabled={!note.trim()} loading={noteMutation.isPending} onClick={() => noteMutation.mutate()}>
                Add note
              </Button>
            </div>
          </div>

          {data.status_history.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-ink">Status history</h3>
              <ol className="mt-3 space-y-2">
                {data.status_history.map((entry) => (
                  <li key={entry.id} className="text-xs text-muted">
                    <span className="font-medium text-ink">
                      {entry.from_status || 'created'} → {entry.to_status}
                    </span>{' '}
                    · {entry.changed_by_name} · {new Date(entry.created_at).toLocaleString('en-IN')}
                    {entry.note && <span className="block pl-2">“{entry.note}”</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export function LeadsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [leadType, setLeadType] = useState('');
  const [page, setPage] = useState(1);
  const [openLead, setOpenLead] = useState<number | null>(null);
  const debouncedSearch = useDebounced(search, 300);

  const listQuery = useQuery({
    queryKey: ['cms', 'leads', { debouncedSearch, status, leadType, page }],
    queryFn: () =>
      cmsApi.leads({
        search: debouncedSearch || undefined,
        status: status || undefined,
        lead_type: leadType || undefined,
        page,
        page_size: 20,
      }),
  });

  const pipelineQuery = useQuery({ queryKey: ['cms', 'lead-pipeline'], queryFn: cmsApi.leadPipeline });

  const columns: Column<LeadRecord>[] = [
    {
      key: 'reference',
      header: 'Reference',
      width: '11rem',
      render: (row) => (
        <button type="button" onClick={() => setOpenLead(row.id)} className="font-mono text-xs font-semibold text-primary underline">
          {row.reference}
        </button>
      ),
    },
    {
      key: 'full_name',
      header: 'Contact',
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">{row.full_name}</p>
          <p className="truncate text-xs text-muted">{row.company || row.email}</p>
        </div>
      ),
    },
    { key: 'lead_type', header: 'Type', width: '10rem', render: (row) => <Badge tone="neutral">{row.lead_type_label}</Badge> },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: '9rem',
      render: (row) => <Badge tone={STATUS_TONES[row.status] ?? 'neutral'}>{row.status_label}</Badge>,
    },
    {
      key: 'assigned_to',
      header: 'Owner',
      width: '9rem',
      render: (row) => <span className="text-muted">{row.assigned_to_name || 'Unassigned'}</span>,
    },
    {
      key: 'created_at',
      header: 'Received',
      sortable: true,
      width: '8rem',
      render: (row) => (
        <span className="text-muted">
          {new Date(row.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
        </span>
      ),
    },
  ];

  if (listQuery.isError) return <LoadError onRetry={() => void listQuery.refetch()} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Leads</h1>
          <p className="mt-1 text-sm text-muted">Every enquiry submitted through the website, with its full history.</p>
        </div>
        <a
          href="/api/leads/export/"
          className="inline-flex h-11 items-center rounded-md border border-line-strong px-5 text-sm font-semibold text-ink no-underline hover:border-primary hover:text-primary hover:no-underline"
        >
          Export CSV
        </a>
      </div>

      {pipelineQuery.data && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Open', value: pipelineQuery.data.open_count },
            { label: 'New', value: pipelineQuery.data.by_status.new ?? 0 },
            { label: 'Unassigned', value: pipelineQuery.data.unassigned_count },
            { label: 'Converted', value: pipelineQuery.data.by_status.converted ?? 0 },
          ].map((tile) => (
            <Card key={tile.label} className="p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-muted">{tile.label}</p>
              <p className="mt-2 font-display text-3xl font-bold text-ink">{tile.value}</p>
            </Card>
          ))}
        </div>
      )}

      <DataTable
        caption="Lead list"
        columns={columns}
        rows={listQuery.data?.results ?? []}
        count={listQuery.data?.count ?? 0}
        page={listQuery.data?.page ?? 1}
        pages={listQuery.data?.pages ?? 1}
        isLoading={listQuery.isLoading}
        search={search}
        searchPlaceholder="Search by reference, name, company or message…"
        filters={[
          {
            key: 'status',
            label: 'Status',
            options: [
              { value: '', label: 'All statuses' },
              { value: 'new', label: 'New' },
              { value: 'contacted', label: 'Contacted' },
              { value: 'qualified', label: 'Qualified' },
              { value: 'in_progress', label: 'In progress' },
              { value: 'converted', label: 'Converted' },
              { value: 'closed', label: 'Closed' },
              { value: 'spam', label: 'Spam' },
            ],
          },
          {
            key: 'lead_type',
            label: 'Type',
            options: [
              { value: '', label: 'All types' },
              { value: 'general', label: 'General' },
              { value: 'demo', label: 'Demo' },
              { value: 'quotation', label: 'Quotation' },
              { value: 'sales', label: 'Sales' },
              { value: 'partnership', label: 'Partnership' },
              { value: 'support', label: 'Support' },
              { value: 'media', label: 'Media' },
            ],
          },
        ]}
        filterValues={{ status, lead_type: leadType }}
        emptyTitle="No leads match this view"
        emptyDescription="Enquiries submitted through the website appear here immediately."
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        onFilter={(key, value) => {
          if (key === 'status') setStatus(value);
          if (key === 'lead_type') setLeadType(value);
          setPage(1);
        }}
        onPage={setPage}
      />

      {openLead !== null && <LeadDrawer leadId={openLead} onClose={() => setOpenLead(null)} />}
    </div>
  );
}
